import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import SEO, { pageSEO, schemas } from "../components/SEO";

const faqs = [
  {
    question: "Who is this trip designed for?",
    answer: "This journey is crafted for curious, comfort-seeking travelers in their mid-30s to 60s — particularly those from the U.S. East Coast experiencing India for the first time. You should be in reasonably good health and able to walk moderate distances, climb stairs, and handle varying temperatures. No prior India experience is required."
  },
  {
    question: "What is the group size?",
    answer: "We keep groups intentionally small — typically 6-8 guests maximum. This ensures a personalized, intimate experience where everyone feels like a friend, not a number. It also allows us to access experiences and venues that larger groups cannot."
  },
  {
    question: "What's the physical activity level?",
    answer: "This is a moderate activity trip. You should be comfortable walking 2-3 miles over the course of a day, climbing stairs at monuments, and standing for extended periods. We pace the itinerary thoughtfully with rest time built in. If you have specific mobility concerns, please discuss them with us."
  },
  {
    question: "Is India safe? How do you handle safety and hygiene?",
    answer: "Safety is our top priority. We use only vetted, licensed drivers and guides, stay at top-tier hotels with security, and carefully manage logistics so you're never in uncomfortable situations. For hygiene, we select restaurants known for high standards, provide bottled water throughout, and offer guidance on what to eat and avoid."
  },
  {
    question: "What about food and dietary needs?",
    answer: "Indian cuisine is diverse and delicious! We feature a mix of iconic restaurants and carefully selected local spots. All properties serve international options alongside Indian food. Vegetarian options are plentiful (India has the largest vegetarian population in the world). Please inform us of allergies, dietary restrictions, or preferences in advance."
  },
  {
    question: "What's included and what's not?",
    answer: "Included: 10 nights accommodation in 5-star and palace hotels, daily breakfast, three signature dinners (Bukhara, Suvarna Mahal, Ana Rasa), select lunches, private transportation, domestic flights, all entrance fees, expert guides, your host Aaron throughout, yoga session, cultural show, and more. Not included: international flights, travel insurance, most lunches, some dinners, personal expenses, tips, spa treatments, and visa fees. See our Pricing page for complete details."
  },
  {
    question: "What is the payment and cancellation policy?",
    answer: "A $1,500 deposit secures your spot. The remaining balance is due 90 days before departure. The deposit is fully refundable until 45 days before departure. After that, cancellation fees apply based on timing. We strongly recommend purchasing travel insurance. Full policy details provided upon inquiry."
  },
  {
    question: "Do I need a visa for India?",
    answer: "Yes, U.S. citizens need a visa. We recommend the e-Visa, which can be applied for online. Processing typically takes 3-5 business days. We provide step-by-step guidance and will help ensure your application goes smoothly."
  },
  {
    question: "Do you help with flights from New York/New Jersey?",
    answer: "While international flights are not included in the trip price, we provide recommendations for the best routing from NYC-area airports to Delhi. We can also coordinate timing to ensure smooth airport pickups regardless of which flight you choose."
  },
  {
    question: "What if I've been to India before? Can I still join?",
    answer: "Absolutely! While we design the journey with first-timers in mind, returning travelers often discover new perspectives through our curated experiences and local connections. Our focus on depth over checklist-ticking means even India veterans find fresh insights."
  },
  {
    question: "What happens if I'm traveling solo?",
    answer: "Solo travelers are very welcome. You'll be paired with another solo traveler for room sharing (same gender), or you can pay the single supplement for a private room. Many of our guests travel solo and leave with new friendships."
  },
  {
    question: "What should I pack?",
    answer: "We provide a detailed packing list after booking. Generally: light, breathable layers, comfortable walking shoes, modest clothing for temple visits, sun protection, and a warm layer for the Himalayas. Laundry service is available at hotels. Pack light — you won't need formal attire."
  },
  {
    question: "Is the elephant ride ethical?",
    answer: "We're very mindful of animal welfare. We work with operators who have demonstrated better practices, and we time visits to avoid extreme heat. The experience is optional, and we're happy to discuss alternatives if you prefer not to participate."
  },
  {
    question: "What's the weather like?",
    answer: "Our departures are timed for optimal weather. October-March offers pleasant temperatures (60-80°F in most areas). The Himalayas will be cooler (40-60°F). We avoid the monsoon season and extreme summer heat. Specific weather briefings are provided before departure."
  },
  {
    question: "How is this different from other India tours?",
    answer: "We combine the insider knowledge of someone born in India with 35+ years of understanding American travelers. Our small groups, palace-level accommodations, signature dining experiences, and deeply personal hosting create a journey that feels like traveling with a knowledgeable friend — not a tour bus experience."
  }
];

export default function FAQsPage() {
  return (
    <div className="pt-24 md:pt-32" data-testid="faqs-page">
      <SEO {...pageSEO.faqs} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Got Questions?</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Frequently Asked <span className="text-[#D4AF37]">Questions</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Everything you need to know before embarking on your India journey.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-[#D4AF37]/20 overflow-hidden shadow-soft"
                  data-testid={`faq-${index}`}
                >
                  <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-[#F9F9F7]/50 text-left">
                    <span className="font-serif text-lg text-[#243b5a] pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <p className="text-[#4A4A4A] leading-relaxed">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mb-6">
            Still Have <span className="text-[#D4AF37]">Questions</span>?
          </h2>
          <p className="text-[#4A4A4A] text-lg max-w-2xl mx-auto mb-8">
            We're happy to answer any other questions you might have about the journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-gold" data-testid="contact-us-faq-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button className="btn-secondary">
                See How It Works
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
